/**
 * The review wall.
 *
 * Reads `assets/airflux-reviews.json` — published nightly by the Worker from
 * Judge.me — and fills the <template>s the section rendered. No markup is built
 * here on purpose: review bodies are stranger-written text, so they are only
 * ever assigned with textContent, and the card's design stays in the theme where
 * it can be edited.
 *
 * Data source, and why:
 *   · assets/airflux-reviews.json — static, on Shopify's CDN, cached across
 *     sessions. No Judge.me request from the browser, so no third-party script,
 *     no token in the theme, and nothing in the critical path that can be slow.
 *
 * The trust numbers above this — the score, the count, the star distribution —
 * are NOT drawn here. They are server-rendered from a generated Liquid snippet,
 * so they are present at first paint and do not move the page when this lands.
 * If this script never runs, or the file 404s, the summary is still correct and
 * the wall simply removes itself.
 *
 * On a product page the page's OWN reviews are listed first under their own
 * heading, then the rest of the category. That order is the whole point: the
 * reviews that are literally about this set are the ones that convert, and the
 * pooled ones underneath must be visibly labelled as other people's cars rather
 * than passed off as claims about this product.
 *
 * No build step, no dependencies.
 */
(() => {
  const CACHE_KEY = 'airflux:reviews';

  /** One in-flight fetch per URL, shared by every instance on the page. */
  const inflight = new Map();

  function load(url) {
    if (inflight.has(url)) return inflight.get(url);

    // Keyed on the asset URL, which carries Shopify's `?v=` cache-buster — the
    // nightly publish changes it, so an open tab can never keep serving a copy
    // from before a review was moderated away.
    const cacheKey = `${CACHE_KEY}:${url}`;

    const promise = (async () => {
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) return JSON.parse(cached);
      } catch {
        /* private mode, or a corrupt entry — fall through to the network */
      }

      const res = await fetch(url);
      if (!res.ok) throw new Error(`reviews ${res.status}`);
      const data = await res.json();

      try {
        // Drop any earlier copy first. The key carries the asset's `?v=`, so a
        // publish mints a NEW key and the old one would otherwise sit there for
        // the life of the tab — a tab left open across the nightly publish kept
        // both, and nothing ever evicted the dead one.
        for (const key of Object.keys(sessionStorage)) {
          if (key.startsWith(`${CACHE_KEY}:`) && key !== cacheKey) sessionStorage.removeItem(key);
        }
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      } catch {
        /* quota, or private mode — the copy in memory is enough for this page */
      }
      return data;
    })();

    inflight.set(url, promise);
    return promise;
  }

  const DATE_FMT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  /**
   * The same photo, at a size worth opening fullscreen.
   *
   * The card's thumbnails are published at `width=256` — right for a 96px
   * square and wrong for a viewer that fills the screen. Judge.me serves any
   * width off the same URL, so the viewer asks for a big one rather than the
   * pipeline shipping two URLs per photo and doubling the file everyone
   * downloads.
   *
   * 1600 rather than Judge.me's own 1024 `huge`: a phone at DPR 3 is already
   * past 1024 across, and this is the one place the photograph is the content.
   */
  const FULL_WIDTH = 1600;
  const fullSize = (url) => {
    try {
      const u = new URL(url);
      u.searchParams.set('width', String(FULL_WIDTH));
      return u.toString();
    } catch {
      return url;
    }
  };

  function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(`${iso}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? '' : DATE_FMT.format(d);
  }

  class AirfluxReviews extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-reviews-list]');
      this.moreBtn = this.querySelector('[data-reviews-more]');
      this.noneMsg = this.querySelector('[data-reviews-none]');
      this.cardTpl = this.querySelector('[data-reviews-card]');
      this.starsTpl = this.querySelector('[data-reviews-stars]');
      this.dividerTpl = this.querySelector('[data-reviews-divider]');
      if (!this.list || !this.cardTpl || !this.starsTpl) return;

      this.pool = this.dataset.pool || 'all';
      this.handle = this.dataset.product || '';
      this.pageSize = Math.max(1, parseInt(this.dataset.pageSize, 10) || 6);
      this.moreLabel = this.dataset.moreLabel || 'Show more';
      this.ownHeading = this.dataset.ownHeading || '';
      this.poolHeading = this.dataset.poolHeading || '';
      // A product page's set is fixed by its URL. A landing page starts with
      // none and takes whichever set the reg box puts on the page — `follow`.
      this.fixed = !!this.handle;
      this.ownOne = this.dataset.ownOne || '';
      this.ownOther = this.dataset.ownOther || '';
      this.ownNone = this.dataset.ownNone || '';
      // The set's line in the summary above, outside this element like the
      // star bars are. Present only on landing pages; product pages render it.
      this.ownLine = this.closest('.af-rvw__panel')?.querySelector('[data-reviews-own]') || null;

      this.filter = 0; // 0 = every rating
      this.shown = 0;

      this.modalSelector = this.dataset.modal || '';
      this.viewFullLabel = this.dataset.viewFullLabel || 'View photo %n';

      this.moreBtn?.addEventListener('click', () => this.showMore());
      this.bindFilters();

      /*
       * Delegated, because cards are rebuilt on every filter and every "show
       * more" — a listener per photo would have to be re-attached each time and
       * would leak the ones on discarded cards.
       */
      this.list.addEventListener('click', (event) => this.openPhoto(event));

      if (!this.fixed) {
        document.addEventListener('airflux:resolved', (event) => this.follow(event.detail));
        // …and whatever the reg box already said before this element existed.
        // Off `?car=` the widget resolves before this deferred script runs, so
        // the event has usually fired by now; the snapshot is the same detail.
        if (window.AirfluxResolved) this.follow(window.AirfluxResolved);
      }

      this.start();
    }

    /**
     * The landing page's wall follows the reg box.
     *
     * 🔴 It did not (2026-09-03): the page resolved a Ford Mondeo, the buy box
     * became the Mondeo set's, and the wall underneath stayed the category's —
     * that set's own review unmarked among the rest, and "This exact set"
     * never said. The event carries the set's handle; everything else is the
     * same split and paint a product page does, on a handle that can change.
     * Off a car we cannot sell, or "Not your car?", the handle is blank and
     * the wall is the category's again.
     */
    follow(detail) {
      const next = detail && detail.buyable && detail.product ? String(detail.product) : '';
      if (next === this.handle) return;
      this.handle = next;
      // Before the fetch has landed, `start()` reads the handle itself.
      if (!this.data) return;
      this.split();
      this.render();
    }

    /**
     * A review photo, opened fullscreen in the product gallery's own viewer.
     *
     * Silently does nothing when there is no viewer on the page or the engine
     * has not loaded — the thumbnail is still a photograph, and a card that
     * cannot zoom is not a broken card. Nothing else on this section depends on
     * it, which is why this never throws.
     */
    openPhoto(event) {
      const btn = event.target.closest?.('.af-rvw-card__photo-btn');
      if (!btn || !this.modalSelector || !window.AirfluxLightbox) return;

      const wrap = btn.closest('[data-slot="photos"]');
      let images = [];
      try {
        images = JSON.parse(wrap?.dataset.photos || '[]');
      } catch {
        return;
      }
      if (!images.length) return;

      // The LAST match: `ModalDialog` moves itself to `<body>` on connect, so a
      // section re-rendered by the theme editor leaves its old viewer behind
      // sharing the same id. The newest is this render's.
      const modals = document.querySelectorAll(this.modalSelector);
      const modal = modals[modals.length - 1];
      if (!modal) return;

      window.AirfluxLightbox.open({
        modal,
        images,
        index: Number(btn.dataset.photoIndex) || 0,
        opener: btn,
      });
    }

    /** The star bars live in the summary above, outside this element. */
    bindFilters() {
      const panel = this.closest('.af-rvw__panel');
      this.filterBtns = panel ? [...panel.querySelectorAll('[data-reviews-filter]')] : [];
      for (const btn of this.filterBtns) {
        btn.addEventListener('click', () => {
          const star = parseInt(btn.dataset.reviewsFilter, 10);
          this.filter = this.filter === star ? 0 : star;
          for (const b of this.filterBtns) {
            b.setAttribute('aria-pressed', String(parseInt(b.dataset.reviewsFilter, 10) === this.filter));
          }
          this.render();
        });
      }
    }

    async start() {
      let data;
      try {
        data = await load(this.dataset.src);
      } catch {
        // The score above is server-rendered and still true, so the honest
        // failure is to drop the wall and leave the summary standing rather
        // than to show a retry nobody asked for.
        this.remove();
        return;
      }

      this.data = data;
      this.split();

      if (!this.own.length && !this.pooled.length) {
        this.remove();
        return;
      }

      this.syncFilters();
      this.render();
    }

    /** This set's cards and the category's, for whatever `handle` is right now. */
    split() {
      const data = this.data;
      const all = data.reviews || [];
      const poolIdx = (data.pool && data.pool[this.pool]) || null;

      // 'all' has no precomputed list — it is every review, newest first, which
      // is the order the file is already in.
      //
      // 🔴 Minus the SHOP reviews. Those are about delivery and service, not
      // about anything a customer can buy, and they carry no car — so on a
      // product page they would sit in a wall of "BMW 1 Series" cards saying
      // nothing about the part. They have their own pool ('s') and their own
      // placement on the About page.
      const pooled = poolIdx ? poolIdx.map((i) => all[i]) : all.filter((r) => r && r.k !== 's');

      const ownIdx = (this.handle && data.byProduct && data.byProduct[this.handle]) || [];
      this.own = ownIdx.map((i) => all[i]).filter(Boolean);

      const ownSet = new Set(this.own);
      this.pooled = pooled.filter((r) => r && !ownSet.has(r));
    }

    /**
     * "This exact set: 5.0 from 1 review" on a landing page, once it has a set.
     *
     * Counted from the cards themselves — the same JSON the wall draws — so
     * the line and the cards under it can never disagree. A product page never
     * reaches this: its line is server-rendered from the metafields its schema
     * claims, and `ownLine` is null there.
     */
    paintOwnLine() {
      if (!this.ownLine) return;
      if (!this.handle) {
        this.ownLine.textContent = '';
        this.ownLine.hidden = true;
        return;
      }
      const n = this.own.length;
      let text = this.ownNone;
      if (n) {
        const score = (this.own.reduce((sum, r) => sum + (Number(r.r) || 0), 0) / n).toFixed(1);
        text = (n === 1 ? this.ownOne : this.ownOther).replace('%s', score).replace('%n', String(n));
      }
      this.ownLine.textContent = text;
      this.ownLine.hidden = !text;
    }

    /**
     * Close off any star filter that has no card behind it.
     *
     * A bar's number is the TRUE distribution, star-only ratings included — a
     * customer who tapped four stars and wrote nothing has rated the product,
     * and the score counts them (see reviews-core's `hasWords`). But there is
     * nothing to draw for one, so a bar can legitimately read 7 while the wall
     * holds 6, and a rating made up entirely of wordless ratings would open an
     * empty wall — the "clicking a filter silently hides everything" failure the
     * pressed state exists to prevent, arriving by a different route.
     *
     * The number stays as it is: it is the honest distribution, and quietly
     * lowering it to the card count would understate the low-star bars, which is
     * exactly the flattering edit this whole section refuses to make. Only the
     * CONTROL closes. And only ever closes — the zero bars are already disabled
     * server-side, and nothing here may re-open one.
     */
    syncFilters() {
      const cards = [...this.own, ...this.pooled];
      for (const btn of this.filterBtns ?? []) {
        if (btn.disabled) continue;
        const star = parseInt(btn.dataset.reviewsFilter, 10);
        if (!cards.some((r) => r.r === star)) btn.disabled = true;
      }
    }

    /** Everything currently passing the star filter, own reviews first. */
    visible() {
      const pass = (r) => !this.filter || r.r === this.filter;
      return { own: this.own.filter(pass), pooled: this.pooled.filter(pass) };
    }

    render() {
      this.shown = this.pageSize;
      this.paint();
    }

    showMore() {
      this.shown += this.pageSize;
      this.paint();
    }

    paint() {
      const { own, pooled } = this.visible();
      const total = own.length + pooled.length;

      this.list.replaceChildren();

      // The budget is shared: this product's own reviews are always drawn first
      // and only what is left over goes to the pooled ones, so "show more" never
      // reveals a stranger's card before one about this exact set.
      const ownSlice = own.slice(0, this.shown);
      const poolSlice = pooled.slice(0, Math.max(0, this.shown - ownSlice.length));

      // Each heading is earned by ITS OWN cards, wherever the page has a set —
      // a product page, or a landing page once the reg box has resolved one.
      // It used to take both groups to earn either, so a set whose only cards
      // were its own carried no "For this exact set", and a set with none of
      // its own showed strangers' cards under no heading at all. Decided on the
      // whole filtered set rather than the page shown, so Show more can never
      // reveal a heading with nothing above it to contrast against.
      const frag = document.createDocumentFragment();

      if (ownSlice.length) {
        if (this.handle && this.ownHeading) frag.append(this.divider(this.ownHeading));
        for (const r of ownSlice) frag.append(this.card(r));
      }

      if (poolSlice.length) {
        // 🔴 Earned by the POOLED cards alone on a product page, own reviews or
        // not. Gated on `grouped`, a set with no reviews of its own drew the
        // category's cards under no heading at all — a Ford Mondeo owner's
        // card on a Fiat 500 page, with nothing saying whose it was (client,
        // 2026-09-03). `handle` is what makes this a product page; a landing
        // page IS the category and keeps its cards unlabelled.
        if (this.handle && this.poolHeading) frag.append(this.divider(this.poolHeading));
        for (const r of poolSlice) frag.append(this.card(r));
      }

      this.list.append(frag);
      this.paintOwnLine();

      const drawn = ownSlice.length + poolSlice.length;

      if (this.noneMsg) this.noneMsg.hidden = total > 0;

      if (this.moreBtn) {
        const remaining = total - drawn;
        this.moreBtn.hidden = remaining <= 0;
        const label = this.moreBtn.querySelector('[data-reviews-more-label]') || this.moreBtn;
        label.textContent = remaining > 0 ? `${this.moreLabel} (${remaining})` : this.moreLabel;
      }
    }

    divider(text) {
      const node = this.dividerTpl.content.cloneNode(true);
      const slot = node.querySelector('[data-slot="text"]');
      if (slot) slot.textContent = text;
      return node;
    }

    /** A star row at an exact rating, cloned from the Liquid-rendered template
     *  so the SVG has one source and the fill maths matches the summary's. */
    stars(rating) {
      const node = this.starsTpl.content.cloneNode(true);
      const el = node.querySelector('.af-stars');
      if (!el) return node;
      const pct = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
      const fill = el.querySelector('.af-stars__fill');
      if (fill) fill.style.inlineSize = `${pct}%`;
      el.setAttribute('aria-label', `${rating} out of 5`);
      return node;
    }

    card(review) {
      const node = this.cardTpl.content.cloneNode(true);
      const slot = (name) => node.querySelector(`[data-slot="${name}"]`);

      slot('stars')?.replaceWith(this.stars(review.r));

      const date = slot('date');
      if (date) {
        const text = formatDate(review.d);
        date.textContent = text;
        if (review.d) date.setAttribute('datetime', review.d);
        date.hidden = !text;
      }

      const title = slot('title');
      if (title && review.t) {
        title.textContent = review.t;
        title.hidden = false;
      }

      const body = slot('body');
      if (body && review.b) {
        body.textContent = review.b;
        body.hidden = false;
      }

      /*
       * `review.p` is a LIST of photo URLs (it was a single string until
       * 2026-09-01, when a two-photo review shipped with one photo showing).
       * The older single-string shape is still accepted so a stale
       * `airflux-reviews.json` — a cached copy, or a theme published before the
       * pipeline change — renders instead of throwing.
       */
      const photos = slot('photos');
      const urls = Array.isArray(review.p) ? review.p : review.p ? [review.p] : [];
      if (photos && urls.length) {
        urls.forEach((url, i) => {
          /*
           * A BUTTON around the photo, not a click handler on the image.
           *
           * The thumbnail is 96px and opens a fullscreen viewer, which is an
           * action — so it has to be reachable by keyboard and announce itself
           * as one. A bare `<img>` with a listener is neither: a keyboard user
           * cannot reach the photographs at all, and a screen-reader user is
           * told there is a picture but not that anything happens if they
           * press it.
           *
           * `viewFull` carries the position because "Open photo" three times in
           * a row tells somebody nothing about which one they are on.
           */
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'af-rvw-card__photo-btn';
          btn.dataset.photoIndex = String(i);
          btn.setAttribute('aria-label', (this.viewFullLabel || 'View photo %n').replace('%n', String(i + 1)));

          const img = document.createElement('img');
          img.className = 'af-rvw-card__photo';
          img.src = url;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          img.width = 96;
          img.height = 96;

          btn.append(img);
          photos.append(btn);
        });
        photos.hidden = false;
        // The URLs the viewer will show, kept on the element rather than
        // re-read from the `<img>`s — those are the 256px thumbnails, and the
        // viewer wants the full-size original.
        photos.dataset.photos = JSON.stringify(urls.map(fullSize));
      }

      /*
       * The shop's answer, when Judge.me holds one — `y` on the published
       * review. Both halves are guarded: the panel only unhides when there is
       * text to put in it, so a card with no reply keeps the markup it already
       * had and shows nothing.
       *
       * `textContent`, like every other slot here. The pipeline strips the tags
       * off Judge.me's HTML before publishing, so what arrives is plain text and
       * is written as plain text — nothing from a third party is ever parsed as
       * markup in this file.
       */
      const reply = slot('reply');
      const replyBody = slot('reply-body');
      if (reply && replyBody && review.y) {
        replyBody.textContent = review.y;
        reply.hidden = false;
      }

      const name = slot('name');
      if (name) name.textContent = review.n || '';

      const verified = slot('verified');
      if (verified) verified.hidden = !review.v;

      const car = slot('car');
      if (car && review.c) {
        car.textContent = review.c;
        car.hidden = false;
      }

      return node;
    }
  }

  if (!customElements.get('airflux-reviews')) {
    customElements.define('airflux-reviews', AirfluxReviews);
  }
})();
